import { CommonModule } from '@angular/common';
import {
  AfterRenderPhase,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  afterRender,
  computed,
  effect,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { LiveClient, SOCKET_STATES, createClient } from '@deepgram/sdk';
import { DateTime } from 'luxon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { DeviceDetectorService } from 'ngx-device-detector';
import { posthog } from 'posthog-js';
import { Subject, Subscription, debounceTime, interval, take } from 'rxjs';
import { PingInterviewErrCodes } from '../../../../../backend/src/core/interview/interview.gate';
import { GetAnswerRequest } from '../../../../../backend/src/core/openai/openai.gate';
import { SuccessResponse } from '../../../../../backend/src/core/request-response';
import { QuestionButtonComponent } from '../../basic/buttons/question-button/question-button.component';
import { ImageOpenInFullComponent } from '../../basic/image/image-open-in-full/image-open-in-full.component';
import { TextImageModalComponent } from '../../basic/modal/text-image-modal/text-image-modal.component';
import { MarkdownItPipe } from '../../basic/pipes/markdown-it.pipe';
import { DeepgramService } from '../../core/deepgram/deepgram.service';
import { Goal } from '../../core/goal/goal';
import {
  FREE_TRIAL_MINS,
  Interview,
  InterviewAnswerMode,
  InterviewCreativity,
  InterviewPerformance,
  InterviewType,
  SESSION_LENGTH,
  canFreeTrial,
  isCreditsEnoughForInterviewType,
} from '../../core/interview/interview';
import { OpenaiMessage, OpenaiModel } from '../../core/openai/openai';
import { PostHogEvent } from '../../core/posthog/posthog.event';
import { Resume } from '../../core/resume/resume';
import { ResumeService } from '../../core/resume/resume.service';
import { InterviewSurveyComponent } from '../../domain/interview-copilot/interview-survey/interview-survey.component';
import { EnumUtils } from '../../shared/utils/enum-utils';
import { InterviewSidebarComponent } from '../home/interview-sidebar/interview-sidebar.component';
import { CoreItemService } from './../../core/core-item/core-item.service';
import { GoalService } from './../../core/goal/goal.service';
import { InterviewMessageService } from './../../core/interview-message/interview-message.service';
import { InterviewService } from './../../core/interview/interview.service';
import { OpenaiService } from './../../core/openai/openai.service';
import { UserClerkService } from './../../core/user/user-clerk.service';
import { WebSocketSendService } from './../../core/websocket/websocket-send.service';
import { WebSocketService } from './../../core/websocket/websocket.service';
import { getDiffPrompt } from './prompt-answer-diff';
import { getInterviewPrompt } from './prompt-interview';

@Component({
  selector: 'app-interview-copilot-run',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzButtonModule,
    NzListModule,
    NzSelectModule,
    NzCommentModule,
    NzAvatarModule,
    NzCheckboxModule,
    NzModalModule,
    NzAlertModule,
    NzSegmentedModule,
    NzTagModule,
    NzIconModule,
    NzTabsModule,
    NzSwitchModule,
    NzStepsModule,
    NzMessageModule,
    NzImageModule,
    NzPopconfirmModule,

    MatTooltipModule,

    MarkdownItPipe,

    InterviewSidebarComponent,
    ImageOpenInFullComponent,
    QuestionButtonComponent,
    InterviewSurveyComponent,
    TextImageModalComponent,
  ],
  templateUrl: './interview-copilot-run.component.html',
  styleUrl: './interview-copilot-run.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterviewCopilotRunComponent {
  // user_2hf7e29rvDppdvcllRYYPHROQZq
  readonly testEmail = 'test01+clerk_test@example.com';

  private readonly RECONNECT_INTERVAL = 1000;

  aiResponseProcessStoppedTime?: DateTime;
  hasAiStreamingResponse = false;

  stopWorking = false;

  everySecond$ = interval(2000); // Create an observable that emits a value every second
  everySecondSubscription?: Subscription;

  everyTwoMinutes$ = interval(60000 * 2);
  everyTwoMinutesSubscription?: Subscription;

  freeTrialWarning = signal<string>('You are in the free trial. 10 minutes remaining.');
  freeTrialMinsLeft = signal<number>(10);

  interviewWarning = signal<string>('');
  interviewMinsLeft = signal<number>(SESSION_LENGTH);

  showInterviewEndModal = signal<boolean>(false);

  // interview id
  @Input() id?: string;

  codeTabIndex = 0;

  configStepIndex = 0;

  ConnectionStatus = ConnectionStatus;
  InterviewType = InterviewType;
  CodingResponseType = CodingResponseType;
  OpenAIStreamStatus = OpenAIStreamStatus;
  InterviewPerformance = InterviewPerformance;

  isAutoScroll = true;
  isPause = signal<boolean>(false);

  isTabOn = signal<boolean>(false);
  isMicOn = signal<boolean>(false);

  tabMedia?: MediaStream;

  isStoppedSharingTab = signal<boolean>(false);

  interview?: Interview;
  goal?: Goal;
  resume?: Resume;

  minsUsed = signal<number>(0);

  myAnswerDiff = signal<string>('');

  isConfig = true;

  isNeedGoalVisible = false;
  isNeedResumeVisible = false;

  isFreeTrialInterview = false;

  microphoneList = signal<MediaDeviceInfo[]>([]);

  selectedMicrophone?: MediaDeviceInfo;

  interviewerAudioTracks?: MediaStreamTrack[];

  private aiResponseDebounced = new Subject<void>();

  private aiResponseDebouncedSubscription?: Subscription;
  private talkToLlmDebouncedSubscription?: Subscription;
  private myAnswerDebouncedSubscription?: Subscription;

  tabSocket?: LiveClient;
  micSocket?: LiveClient;

  deepgramKey?: string;

  codingTabs = EnumUtils.getAllValues(CodingResponseType);

  pingInterviewCount = 0;

  analyzingImage = signal<boolean>(false);

  has0FreeTrialWarning = false;

  has15Warning = false;
  has5Warning = false;
  has1Warning = false;
  has0Warning = false;

  user = toSignal(this.userClerkService.getUser$());

  constructor(
    private deepgramService: DeepgramService,
    private msg: NzMessageService,
    private ref: ChangeDetectorRef,
    private router: Router,
    private modalService: NzModalService,
    private goalService: GoalService,
    private deviceService: DeviceDetectorService,
    private openaiService: OpenaiService,
    private resumeService: ResumeService,
    private userClerkService: UserClerkService,
    private webSocketService: WebSocketService,
    private interviewService: InterviewService,
    private webSocketSendService: WebSocketSendService,
    private interviewMessageService: InterviewMessageService,
    private coreItemService: CoreItemService
  ) {
    this.everySecondSubscription = this.everySecond$.subscribe(() => {
      if (!this.interview) {
        return;
      }

      if (this.isFreeTrialInterview) {
        const dateStr = this.user()!.publicMetadata['freeTrialDateStr'] as string;
        const date = DateTime.fromISO(dateStr!);

        this.minsUsed.set(Math.floor(-date.diffNow('minutes').minutes));

        const diff = Math.ceil(FREE_TRIAL_MINS + date.diffNow('minutes').minutes);

        this.freeTrialWarning.set(
          `Your free trial will end in ${diff} minutes. You can purchase points to unlock full 60-minute interviews with complete feature access.`
        );

        this.freeTrialMinsLeft.set(diff);

        if (diff <= 0) {
          if (!this.has0FreeTrialWarning) {
            this.pauseInterview();

            posthog.capture(PostHogEvent.interviewFreeTrialExpired, {
              interviewId: this.interview!.id,
            });

            this.interviewService.showNotEnoughPointsModal.set(true);
            this.router.navigate(['/interview']);
          } else {
            this.has0FreeTrialWarning = true;
          }
        }
      } else {
        const startDate = this.interview.startTime;

        if (!startDate) {
          posthog.capture(PostHogEvent.interviewNoStartTime, {
            interviewId: this.interview!.id,
          });
          return;
        }

        this.minsUsed.set(Math.floor(-startDate.diffNow('minutes').minutes));

        const diff = Math.ceil(SESSION_LENGTH + startDate.diffNow('minutes').minutes);
        this.interviewMinsLeft.set(diff);

        if (diff === 15) {
          if (!this.has15Warning) {
            this.msg.warning(
              'The current interview will be terminated in 15 minutes. Please manage your time properly. You can start a new interview if needed.',
              { nzDuration: 180000 }
            );
            this.has15Warning = true;
          }
        } else if (diff === 5) {
          if (!this.has5Warning) {
            this.msg.warning(
              'The current interview will be terminated in 5 minutes. Please manage your time properly. You can start a new interview if needed.',
              { nzDuration: 180000 }
            );
            this.has5Warning = true;
          }
        } else if (diff === 1) {
          if (!this.has1Warning) {
            this.msg.warning(
              'The current interview will be terminated in 1 minutes. Please manage your time properly. You can start a new interview if needed.',
              { nzDuration: 180000 }
            );
            this.has1Warning = true;
          }
        } else if (diff === 0) {
          if (!this.has0Warning) {
            this.pauseInterview();
            this.showInterviewEndModal.set(true);
          } else {
            this.has0Warning = true;
          }
        }
      }

      if (this.hasAiStreamingResponse && this.aiResponseProcessStoppedTime) {
        const timeDiff = -this.aiResponseProcessStoppedTime.diffNow('seconds').seconds;

        if (timeDiff > 20) {
          this.stopWorking = true;

          posthog.capture(PostHogEvent.interviewException, {
            interview: this.interview!.id,
          });
        }
      }
    });

    this.everyTwoMinutesSubscription = this.everyTwoMinutes$.subscribe(() => {
      if (!this.interview) {
        return;
      }

      this.interviewService
        .pingInterview$(this.interview)
        .pipe(take(1))
        .subscribe({
          next: (data: SuccessResponse) => {
            if (!data.isSuccess) {
              this.pauseInterview();

              posthog.capture(PostHogEvent.interviewPingError, {
                errCode: data.errCode,
                errMessage: data.errMessage,
                interview: this.interview!.id,
              });

              if (data.errCode === PingInterviewErrCodes.NOT_FOUND) {
                this.msg.error(`Cannot find the interview: ${this.id}.`, {
                  nzDuration: 10000,
                });
              } else if (data.errCode === PingInterviewErrCodes.NO_CREDITS) {
                this.msg.error(
                  "You don't have enough points to launch the interview. You can purchase more points from the store.",
                  { nzDuration: 10000 }
                );
                this.interviewService.showNotEnoughPointsModal.set(true);
              } else if (data.errCode === PingInterviewErrCodes.NO_START_TIME_STR) {
                this.msg.error('The interview status is invalidated. Return to the interview list page.', {
                  nzDuration: 10000,
                });
              } else if (data.errCode === PingInterviewErrCodes.STATUS_NOT_ONGOING) {
                this.msg.error(`The interview status is invalidated. Return to the interview list page.`, {
                  nzDuration: 10000,
                });
              } else {
                this.msg.error('Unknown error. Return to the interview list page.', { nzDuration: 10000 });
              }
              this.router.navigate(['/interview']);
            }
          },
          error: (err) => {
            posthog.capture(PostHogEvent.interviewPingException, {
              error: err?.message,
              interviewId: this.interview!.id,
            });

            this.pingInterviewCount++;

            if (this.pingInterviewCount >= 5) {
              this.pauseInterview();
              this.msg.error('Cannot reach to the server. Return to the interview list page.', { nzDuration: 10000 });
              this.router.navigate(['/interview']);
            }
          },
        });
    });

    afterRender(
      () => {
        if (this._shouldScrollToBottom) {
          this.scrollAllToBottom();
          this._shouldScrollToBottom = false;
        }
      },
      { phase: AfterRenderPhase.Read }
    );

    effect(() => {
      const microphoneList = this.microphoneList();

      if (microphoneList.length > 0) {
        this.configStepIndex = 1;
      }
    });

    effect(
      () => {
        const isTabOn = this.isTabOn();
        const isMicOn = this.isMicOn();

        if (isTabOn && isMicOn) {
          this.isPause.set(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const streamingContent = this.webSocketService.streamingChat();

        if (streamingContent) {
          this.hasAiStreamingResponse = true;
          this.aiResponseProcessStoppedTime = DateTime.local();
        }

        if (streamingContent?.finishedReason === 'stop') {
          this.streamStatus.set(OpenAIStreamStatus.pending);
          this.hasAiStreamingResponse = false;
          if (typeof streamingContent.additionalId === 'number') {
            this.chatStreamStatus.set(OpenAIStreamStatus.pending);
            if (this.aiResponseList().length < this.interviewerList().length) {
              this.aiResponseList.update((data) => {
                return [...data, ''];
              });
            }
          }
          // else if (streamingContent.additionalId === CodingResponseType.idea) {
          //   this.ideaResponseList.update((data) => {
          //     return [...data, ''];
          //   });
          // } else if (streamingContent.additionalId === CodingResponseType.code) {
          //   this.codeResponseList.update((data) => {
          //     return [...data, ''];
          //   });
          // } else if (streamingContent.additionalId === CodingResponseType.testCases) {
          //   this.testCasesResponseList.update((data) => {
          //     return [...data, ''];
          //   });
          // }
        } else if (streamingContent && streamingContent?.coreId === this.interview?.id) {
          if (typeof streamingContent.additionalId === 'number') {
            this.aiResponseList.update((data) => {
              if (data.length === 0) {
                return [streamingContent!.content];
              } else {
                const dataIndex = streamingContent.additionalId as number;

                if (dataIndex > data.length - 1) {
                  return [...data, streamingContent!.content];
                } else {
                  const lastContent = data[dataIndex];
                  data[dataIndex] = `${lastContent}${streamingContent!.content}`;
                  return [...data];
                }
              }
            });
          } else if (streamingContent.additionalId === CodingResponseType.idea) {
            this.codeIdeaList.update((data) => {
              if (data.length === 0) {
                return [streamingContent!.content];
              } else {
                const lastContent = data[data.length - 1];
                data[data.length - 1] = `${lastContent}${streamingContent!.content}`;
                return [...data];
              }
            });
          } else if (streamingContent.additionalId === CodingResponseType.code) {
            this.codeSolutionList.update((data) => {
              if (data.length === 0) {
                return [streamingContent!.content];
              } else {
                const lastContent = data[data.length - 1];
                data[data.length - 1] = `${lastContent}${streamingContent!.content}`;
                return [...data];
              }
            });
          } else if (streamingContent.additionalId === CodingResponseType.testCases) {
            this.codeTestCasesList.update((data) => {
              if (data.length === 0) {
                return [streamingContent!.content];
              } else {
                const lastContent = data[data.length - 1];
                data[data.length - 1] = `${lastContent}${streamingContent!.content}`;
                return [...data];
              }
            });
          }

          this.aiResponseDebounced.next();

          if (this.isAutoScroll) {
            this._shouldScrollToBottom = true;
          }
        }
      },
      { allowSignalWrites: true }
    );

    this.aiResponseDebouncedSubscription = this.aiResponseDebounced.pipe(debounceTime(3000)).subscribe(() => {
      this.hasAiStreamingResponse = false;
      this.aiResponseList.update((data) => {
        if (data.length === 0) {
          return [''];
        } else {
          if (this.aiResponseList().length < this.interviewerList().length) {
            return [...data, ''];
          } else {
            return data;
          }
        }
      });
    });

    this.talkToLlmDebouncedSubscription = this.talkToLlmDebounced.pipe(debounceTime(4000)).subscribe(() => {
      // this.talkToLlm();
    });

    this.myAnswerDebouncedSubscription = this.myAnswerDebounced.pipe(debounceTime(2000)).subscribe(() => {
      this.compareMyAnswerDiff();
    });
  }

  isTestEmail() {
    return this.user()!.primaryEmailAddress?.emailAddress === this.testEmail;
  }

  onPauseChange(isPaused: boolean) {
    if (isPaused) {
      posthog.capture(PostHogEvent.interviewPausedByHuman, {
        interview: this.interview!.id,
      });

      this.pauseInterview();
    } else {
      this.getMic();
      this.getTab();
    }
  }

  private pauseInterview() {
    this.isPause.set(true);
    if (this.micSocket?.getReadyState() === SOCKET_STATES.open) {
      this.micSocket.requestClose();
    }

    if (this.tabSocket?.getReadyState() === SOCKET_STATES.open) {
      this.tabSocket?.requestClose();
    }
  }

  onRefreshPageClick() {
    window.location.reload();
  }

  compareMyAnswerDiff() {
    const myAnswerList = this.myAnswerList();
    const aiResponseList = this.aiResponseList();
    if (myAnswerList.length > 0 && aiResponseList.length > 0) {
      const myAnswer = myAnswerList[myAnswerList.length - 1];

      let idealAnswerIndex = aiResponseList.length - 1;
      let idealAnswer = aiResponseList[idealAnswerIndex];

      if (!idealAnswer) {
        if (aiResponseList.length > 1) {
          idealAnswerIndex = aiResponseList.length - 2;
          idealAnswer = aiResponseList[idealAnswerIndex];
        }
      }

      if (myAnswer.length > 150) {
        idealAnswer = idealAnswer?.replaceAll('<span class="text-highlight">', '')?.replaceAll('</span>', '');

        const prompt = getDiffPrompt(myAnswer, idealAnswer);

        const func = {
          name: 'selectUncoveredParts',
          description: 'Select uncovered parts based on the ideal response.',
          parameters: {
            type: 'object',
            properties: {
              unconveredParts: {
                type: 'array',
                items: {
                  type: 'string',
                },
              },
            },
          },
        };

        const request: GetAnswerRequest = {
          messageList: [{ role: 'user', content: prompt }],
          config: {
            model: OpenaiModel.gpt35,
            functions: [func],
          },
        };

        this.openaiService
          .getAnswer$(request, true)
          .pipe(take(1))
          .subscribe((data) => {
            if (data?.answer?.function_call?.arguments) {
              const jsonResponse = JSON.parse(data.answer.function_call.arguments);

              const unconveredPartStr: string = jsonResponse?.unconveredParts?.join(' ') ?? '';

              if (unconveredPartStr.length / idealAnswer.length < 0.4) {
                jsonResponse?.unconveredParts.forEach((str: string) => {
                  idealAnswer = idealAnswer.replaceAll(str, `<span class="text-highlight">${str}</span>`);
                });

                this.aiResponseList.update((data) => {
                  data[idealAnswerIndex] = idealAnswer;
                  return [...data];
                });
              }
            }
          });
      }
    }
  }

  refreshPage() {
    location.reload();
  }

  selectedPerformanceIndex?: number;
  performanceOptions = EnumUtils.getAllValues(InterviewPerformance);

  selectedCreativityIndex?: number;
  creativityOptions = EnumUtils.getAllValues(InterviewCreativity);

  selectedAnswerModeIndex?: number;
  answerModeOptions = EnumUtils.getAllValues(InterviewAnswerMode);

  showWatermark = false;

  ngOnInit() {
    if (!this.id) {
      this.msg.error('Cannot find interview id.', { nzDuration: 10000 });
      this.router.navigate(['/interview']);
    }

    if (this.deviceService.isMobile()) {
      this.msg.error('The mobile is not supported yet.', { nzDuration: 10000 });
      this.router.navigate(['/interview']);
    }

    this.hasAiStreamingResponse = false;
    this.stopWorking = false;

    this.isFreeTrialInterview = false;

    this.minsUsed.set(0);
    this.interviewMinsLeft = signal<number>(SESSION_LENGTH);

    this.showInterviewEndModal.set(false);

    this.isPause.set(false);
    this.isTabOn.set(false);
    this.isMicOn.set(false);
    this.isStoppedSharingTab.set(false);

    this.has0FreeTrialWarning = false;

    this.has15Warning = false;
    this.has5Warning = false;
    this.has1Warning = false;
    this.has0Warning = false;

    this.showWatermark = this.isTestEmail();

    this.interviewService
      .getInterviewById$(this.id!)
      .pipe(take(1))
      .subscribe((data) => {
        this.interview = data;

        if (!this.interview) {
          this.msg.error('Interview not found. Please create one first.', { nzDuration: 10000 });
          this.router.navigate(['/interview']);
        }

        if (!isCreditsEnoughForInterviewType(this.interview.type, this.user()!.publicMetadata['credits'] as number)) {
          if (
            this.interview.id === this.user()!.publicMetadata['freeTrialInterviewId'] ||
            canFreeTrial(this.user()!.publicMetadata)
          ) {
            // Free trial interview
            this.isFreeTrialInterview = true;
          } else {
            this.msg.error("You don't have enough points to launch the interview. Return to the interview list page.", {
              nzDuration: 10000,
            });
            this.interviewService.showNotEnoughPointsModal.set(true);
            this.router.navigate(['/interview']);
          }
        }

        if (this.interviewService.isOngoing(this.interview)) {
          this.selectedPerformanceIndex = EnumUtils.getEnumIndex(
            this.interview.subData.performance,
            InterviewPerformance
          );
          this.selectedCreativityIndex = EnumUtils.getEnumIndex(this.interview.subData.creativity, InterviewCreativity);
          this.selectedAnswerModeIndex = EnumUtils.getEnumIndex(this.interview.subData.answerMode, InterviewAnswerMode);

          this.goalService
            .getGoalById$(this.interview.subData.goalId)
            .pipe(take(1))
            .subscribe((goal) => {
              if (!goal) {
                this.isNeedGoalVisible = true;
              }

              this.goal = goal;
            });

          this.resumeService
            .getResumeById$(this.interview.subData.resumeId)
            .pipe(take(1))
            .subscribe((resume) => {
              if (!resume) {
                this.isNeedResumeVisible = true;
              }

              this.resume = resume;
            });
        } else {
          this.msg.error('This interview has finished. Return to the interview list page.', { nzDuration: 10000 });
          this.router.navigate(['/interview']);
        }
      });

    this.getMicrophones().then((devices) => {
      this.microphoneList.set(devices);
    });
  }

  async ngOnDestroy() {
    this.pauseInterview();

    this.interviewService.stopTabSharing();
    this.interviewService.stopMicSharing();

    this.everySecondSubscription?.unsubscribe();
    this.aiResponseDebouncedSubscription?.unsubscribe();
    this.talkToLlmDebouncedSubscription?.unsubscribe();
    this.myAnswerDebouncedSubscription?.unsubscribe();
    this.everyTwoMinutesSubscription?.unsubscribe();
  }

  async getMicrophones(): Promise<MediaDeviceInfo[]> {
    try {
      // Request permissions for media devices
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Enumerate devices to get the list of devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter out the devices to get only the microphones
      const microphones = devices.filter((device) => device.kind === 'audioinput');

      if (microphones.length > 0) {
        this.selectedMicrophone = microphones[0];
      }
      return microphones;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      return [];
    }
  }

  onRerunClick(performance: InterviewPerformance) {
    // Remove myAnswerList
    const aiResponseList = this.aiResponseList();

    if (aiResponseList.length > 1) {
      this.aiResponseList.update((data) => {
        return [...data.slice(0, data.length - 2), ''];
      });
      this.talkToLlm(performance, true);
    }
  }

  onGobackClick() {
    this.router.navigate(['interview-assistant']);
  }

  isStartButtonDisabled() {
    return !this.interviewerAudioTracks || !this.goal || !this.resume;
  }

  chatTitle = computed(() => {
    const status = this.chatStreamStatus();
    const title = 'Chat ' + (status === OpenAIStreamStatus.generating ? '[Generating...]' : '');
    return title;
  });

  isEndSurveyVisible = false;

  onEndInterviewClick() {
    if (this.interview) {
      this.everyTwoMinutesSubscription?.unsubscribe();

      if (this.isTestEmail()) {
        this.interviewService
          .endInterview$(this.interview)
          .pipe(take(1))
          .subscribe(() => {
            posthog.capture(PostHogEvent.interviewManuallyEnd, {
              interviewId: this.interview!.id,
              durationMins: -(this.interview!.startTime?.diffNow('minutes').minutes ?? 0),
            });
            this.router.navigate(['', 'interview']);
          });
      } else {
        this.isEndSurveyVisible = true;
        this.interviewService
          .endInterview$(this.interview)
          .pipe(take(1))
          .subscribe(() => {
            posthog.capture(PostHogEvent.interviewManuallyEnd, {
              interview: this.interview!.id,
              durationMins: -(this.interview!.startTime?.diffNow('minutes').minutes ?? 0),
            });
          });
      }
    }
  }

  onEndInterviewSurveyVisibleChange(event: boolean) {
    if (event === false) {
      this.router.navigate(['', 'interview']);
    }
  }

  async onShareBrowserTabClick() {
    try {
      // Define constraints for the screen capture
      const constraints: DisplayMediaStreamOptions = {
        video: true,
        audio: true,
      };

      // Prompt the user to select a screen, window, or tab to share
      const media = await navigator.mediaDevices.getDisplayMedia(constraints);

      this.interviewService.tabMedia = media;

      if (!media || media?.getVideoTracks().length === 0) {
        this.modalService.error({
          nzTitle: 'Please share a tab.',
          nzContent: 'Please share your interview tab.',
        });
        this.interviewService.stopTabSharing();
        return;
      }

      const track = media.getVideoTracks()[0];
      const settings = track.getSettings();

      if (settings.displaySurface !== 'browser') {
        this.modalService.error({
          nzTitle: 'Please share a browser tab',
          nzContent: 'Please share the specific browser tab you have opened with your interviewer.',
        });

        this.interviewService.stopTabSharing();
        return;
      }

      if (media.getAudioTracks().length === 0) {
        this.modalService.error({
          nzTitle: 'Please share your tab with audio.',
          nzContent: 'Please make sure you enabled the audio.',
        });
        return;
      }

      this.isStoppedSharingTab.set(false);

      track.addEventListener('ended', () => {
        this.isStoppedSharingTab.set(true);
      });

      this.interviewerAudioTracks = media?.getAudioTracks();

      this.configStepIndex = 3;

      console.log('------this.isStoppedSharingTab:', this.isStoppedSharingTab());

      this.ref.detectChanges();
    } catch (error) {
      console.error('Error sharing the screen:', error);
    }
  }

  isNeedToShareAudioModalVisible = false;

  onStartClick() {
    this.isConfig = false;

    this.getMic();
    this.getTab();
  }

  // === START ===
  interviewerList = signal<string[]>([]);
  myAnswerList = signal<string[]>([]);

  aiResponseList = signal<string[]>([]);

  codeQuestionList = signal<string[]>([]);
  codeIdeaList = signal<string[]>([]);
  codeSolutionList = signal<string[]>([]);
  codeTestCasesList = signal<string[]>([]);

  connectStatus = signal<ConnectionStatus>(ConnectionStatus.loading);
  connectStatusReason = '';

  micConnectStatus = signal<ConnectionStatus>(ConnectionStatus.loading);
  micConnectStatusReason = signal<string>('');

  streamStatus = signal<OpenAIStreamStatus>(OpenAIStreamStatus.pending);
  chatStreamStatus = signal<OpenAIStreamStatus>(OpenAIStreamStatus.pending);

  private talkToLlmDebounced = new Subject<void>();
  private myAnswerDebounced = new Subject<void>();

  selectedIndex = -1;

  onInterviewerMessageMouseover(i: number) {
    this.selectedIndex = i;
  }

  onAiResponseMessageMouseover(i: number) {
    this.selectedIndex = i;
  }

  onMyAnswerMessageMouseover(i: number) {
    this.selectedIndex = i;
  }

  handleNeedToShareAudioModalOk() {
    location.reload();
  }

  private videoElement?: HTMLVideoElement;

  ngAfterViewInit(): void {
    this.videoElement = document.createElement('video');
    this.videoElement.style.display = 'none'; // Hide the video element
    document.body.appendChild(this.videoElement); // Append to body to ensure playback
  }

  async getCodingAnswer() {
    try {
      this.codeTabIndex = 1;

      const constraints: DisplayMediaStreamOptions = {
        video: true,
        audio: false,
      };

      const codingStream = await navigator.mediaDevices.getDisplayMedia(constraints);

      const stream = codingStream;
      this.videoElement!.srcObject = stream as any;
      this.videoElement!.play();

      this.videoElement!.onloadedmetadata = () => {
        const canvas = document.createElement('canvas');

        if (this.videoElement!.videoWidth > 1500) {
          const ratio = this.videoElement!.videoWidth / 1500;
          canvas.width = 1500;
          canvas.height = this.videoElement!.videoHeight / ratio;
        } else {
          canvas.width = this.videoElement!.videoWidth;
          canvas.height = this.videoElement!.videoHeight;
        }

        const context = canvas.getContext('2d');
        if (context) {
          context.drawImage(this.videoElement!, 0, 0, canvas.width, canvas.height);
          const base64Image = canvas.toDataURL('image/png');

          if (codingStream) {
            codingStream.getTracks().forEach((track) => {
              track.stop();
            });
            console.log('Screen sharing stopped.');
          }

          const message: OpenaiMessage = {
            role: 'user',
            content: [
              {
                type: 'text',
                text: `Given an image that contains a coding problem or question, provide a detailed description of what the coding question is asking. Do not need to answer the code question.`,
              },
              {
                type: 'image_url',
                image_url: {
                  url: base64Image,
                },
              },
            ],
          };

          const model =
            this.performanceOptions[this.selectedPerformanceIndex ?? 0] === InterviewPerformance.Speed
              ? OpenaiModel.gpt35
              : OpenaiModel.gpt4;

          const temperature =
            this.creativityOptions[this.selectedCreativityIndex ?? 0] === InterviewCreativity.Deterministic ? 0.2 : 0.8;

          this.analyzingImage.set(true);
          this.msg.loading('Analyzing code...', { nzDuration: 0 });

          this.openaiService
            .getAnswer$({
              messageList: [message],
              config: { model: OpenaiModel.gpt4, temperature },
            })
            .pipe(take(1))
            .subscribe((data) => {
              this.addCodeMessageData();

              const questionContent = data.answer.content;

              this.codeQuestionList.update((list) => {
                return [...list, questionContent];
              });

              this.codeIdeaList.update((list) => {
                return [...list, ''];
              });

              this.codeSolutionList.update((list) => {
                return [...list, ''];
              });

              this.codeTestCasesList.update((list) => {
                return [...list, ''];
              });

              this.codingTabs.forEach((tab, i) => {
                let prompt = '';
                if (tab === CodingResponseType.code) {
                  prompt = `Using the programming language '${this.interview?.subData.programmingLanguage}', please write the code to solve the following coding question: \n\n ${questionContent}`;
                } else if (tab === CodingResponseType.testCases) {
                  prompt = `Based on the coding question provided below, please write some corner cases and test cases: \n\n ${questionContent}`;
                } else if (tab === CodingResponseType.idea) {
                  prompt = `
Step 1: Provide a step to step of how to teach a total algorithm beginner to approach to this problem with a brutal force solution, run one simple illustration with short dummy input and output, no need to provide code now. Additionally, provide the space complexity and the time complexity.

Step 2: Provide a more optimal solution for this problem, explain the choice of different potential data structures or algorithms, choose a data structure that is easy to use and implement, no need to provide code now. Additionally, provide the space complexity and the time complexity.

The coding question:

${questionContent}

Please keep the answer concise and clear.
`;
                }

                this.streamStatus.set(OpenAIStreamStatus.generating);
                this.webSocketSendService.sendStreamingChat(this.interview!.id, [{ role: 'user', content: prompt }], {
                  model,
                  temperature,
                  additionalId: tab,
                });
              });

              this.analyzingImage.set(false);
              this.msg.remove();
            });
        }
      };
    } catch (error) {
      console.error('Error capturing frame:', error);
    }
  }

  isDismiss = false;
  onDismissClick() {
    this.isDismiss = true;
  }

  private addCodeMessageData() {
    if (this.codeQuestionList().length > 0) {
      // Last item
      const index = this.codeQuestionList().length - 1;
      this.interviewMessageService
        .addInterviewMessage$(this.interview!, {
          codeQuestion: this.codeQuestionList()[index],
          codeIdea: this.codeIdeaList()[index],
          codeSolution: this.codeSolutionList()[index],
          codeTestCases: this.codeTestCasesList()[index],
        })
        .pipe(take(1))
        .subscribe();
    }
  }

  async onReconnectClick() {
    if (this.isStoppedSharingTab()) {
      if (this.tabSocket?.getReadyState() === SOCKET_STATES.open) {
        this.tabSocket?.requestClose();
      }
      this.onShareBrowserTabClick().then(() => {
        this.getTab();
      });
    } else {
      this.getTab();
    }
  }

  onMicReconnectClick() {
    this.getMic();
  }

  getTab() {
    if (!this.interviewerAudioTracks) {
      return;
    }

    this.tabSocket?.removeAllListeners();

    const stream = new MediaStream(this.interviewerAudioTracks);

    const that = this;
    const mediaRecorder = new MediaRecorder(stream);

    this.deepgramService
      .getTempApiKeyForInterview$(this.interview!.id!)
      .pipe(take(1))
      .subscribe((keyResponse) => {
        const key = keyResponse.key;
        const _deepgram = createClient(key);

        that.tabSocket = _deepgram.listen.live({
          model: 'nova-2',
          smart_format: true,
          interim_results: true,
          utterance_end_ms: 3000,
          endpointing: 300,
          no_delay: true,
          language: this.interview!.subData.language,
        });

        const socket = that.tabSocket;

        socket.on('UtteranceEnd', (data: any) => {
          this.talkToLlm();
        });

        socket.on('open', async () => {
          that.connectStatusReason = '';
          that.connectStatus.set(ConnectionStatus.connected);
          this.isTabOn.set(true);

          socket.on('Results', (data: any) => {
            const transcript = data.channel.alternatives[0].transcript;

            if (transcript) {
              that.talkToLlmDebounced.next();
            }

            if (transcript && data.is_final) {
              this.interviewerList.update((data) => {
                if (data.length === 0) {
                  return [transcript];
                } else {
                  const lastContent = data[data.length - 1];
                  data[data.length - 1] = `${lastContent} ${transcript}`;
                  return [...data];
                }
              });
              if (this.isAutoScroll) {
                this._shouldScrollToBottom = true;
              }
            }
          });

          socket.on('error', (e: any) => {
            console.error(e);
            this.isTabOn.set(false);
            this.connectStatusReason = 'Please try to reconnect.'; // e.reason;
            this.connectStatus.set(ConnectionStatus.closed);

            posthog.capture(PostHogEvent.interviewDeepgramError, {
              error: e?.reason,
              interview: this.interview!.id,
            });
          });

          socket.on('warning', (e: any) => {
            console.warn(e);
            this.isTabOn.set(false);
            this.connectStatusReason = 'Please try to reconnect.'; // e.reason;
            this.connectStatus.set(ConnectionStatus.warning);
          });

          socket.on('Metadata', (e: any) => console.log(e));

          socket.on('close', (e: any) => {
            console.log(e);
            this.isTabOn.set(false);
            this.connectStatusReason = 'Please try to reconnect.'; // e.reason;
            this.connectStatus.set(ConnectionStatus.closed);

            posthog.capture(PostHogEvent.interviewDeepgramClosed, {
              event: e?.reason,
              interview: this.interview!.id,
            });
          });

          mediaRecorder.addEventListener('dataavailable', (event) => {
            if (event.data.size > 0) {
              if (socket.getReadyState() === SOCKET_STATES.open) {
                socket.send(event.data);
              }
            }
          });
          mediaRecorder.start(250);
        });
      });
  }

  getMic() {
    if (this.selectedMicrophone) {
      const that = this;
      const chosenDeviceId = this.selectedMicrophone!.deviceId;

      this.micSocket?.removeAllListeners();

      navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: chosenDeviceId } } }).then((stream) => {
        // Further code goes here
        this.interviewService.micStream = stream;

        const mediaRecorder = new MediaRecorder(stream);

        this.deepgramService
          .getTempApiKeyForInterview$(this.interview!.id!)
          .pipe(take(1))
          .subscribe((keyResponse) => {
            const key = keyResponse.key;
            const _deepgram = createClient(key);

            that.micSocket = _deepgram.listen.live({
              model: 'nova-2',
              smart_format: true,
              language: this.interview!.subData.language,
            });

            const socket = that.micSocket;

            socket.on('open', async () => {
              console.log('client: connected to websocket');

              this.micConnectStatusReason.set('');
              this.micConnectStatus.set(ConnectionStatus.connected);
              this.isMicOn.set(true);

              socket.on('Results', (data: any) => {
                const transcript = data.channel.alternatives[0].transcript;

                if (transcript && data.is_final) {
                  this.myAnswerList.update((data) => {
                    if (data.length === 0) {
                      return [transcript];
                    } else {
                      const lastContent = data[data.length - 1];
                      data[data.length - 1] = `${lastContent} ${transcript}`;

                      this.myAnswerDebounced.next();
                      return [...data];
                    }
                  });

                  if (this.isAutoScroll) {
                    this._shouldScrollToBottom = true;
                  }
                }
              });

              socket.on('error', (e: any) => {
                posthog.capture(PostHogEvent.interviewMicError, {
                  error: e?.reason,
                  interview: this.interview!.id,
                });

                // this.micConnectStatusReason.set(e.reason);
                this.micConnectStatusReason.set('Please try to reconnect.');

                this.micConnectStatus.set(ConnectionStatus.error);

                this.isMicOn.set(false);
              });

              socket.on('warning', (e: any) => {
                this.micConnectStatusReason.set('Please try to reconnect.');
                this.micConnectStatus.set(ConnectionStatus.warning);

                this.isMicOn.set(false);
              });

              socket.on('Metadata', (e: any) => console.log(e));

              socket.on('close', (e: any) => {
                posthog.capture(PostHogEvent.interviewMicClosed, {
                  error: e?.reason,
                  interview: this.interview!.id,
                });

                this.micConnectStatusReason.set('Please try to reconnect.');
                this.micConnectStatus.set(ConnectionStatus.closed);

                this.isMicOn.set(false);
              });

              mediaRecorder.addEventListener('dataavailable', (event) => {
                if (event.data.size > 0) {
                  if (socket.getReadyState() === SOCKET_STATES.open) {
                    socket.send(event.data);
                  }
                }
              });
              mediaRecorder.start(250);
            });
          });
      });
    } else {
      this.msg.error('No microphone is detected. Please select a microphone.', { nzDuration: 10000 });
    }
  }

  private talkToLlm(performance?: InterviewPerformance, rerun?: boolean) {
    this.interviewerList.update((data) => {
      let lastContent = data[data.length - 1];

      if (rerun) {
        lastContent = data[data.length - 2];
      }

      const langauge = this.interview!.subData.language;

      if ((langauge === 'en' && lastContent?.length > 20) || (langauge !== 'en' && lastContent?.length > 5)) {
        if (this.aiResponseList().length > 0) {
          let aiResponseIndex = this.aiResponseList().length - 1;
          if (this.aiResponseList().length === this.interviewerList().length) {
            aiResponseIndex = this.aiResponseList().length - 2;
          }

          this.interviewMessageService
            .addInterviewMessage$(this.interview!, {
              interviewer: this.interviewerList()[this.interviewerList().length - 2],
              aiResponse: this.aiResponseList()[aiResponseIndex],
              myAnswer: this.myAnswerList()[this.myAnswerList().length - 1],
            })
            .pipe(take(1))
            .subscribe();
        }

        const language = this.interview!.subData.language;

        const company = this.goal?.subData.company ?? '';
        const position = this.goal?.subData.position ?? '';
        const companyDetails = this.goal?.subData.companyDetails ?? '';
        const jobDescription = this.goal?.subData.jobDescription ?? '';
        const resume = this.resume!.subData.resumeContent ?? '';

        let previousConversationList: string[] = [];
        if (data.length > 1) {
          const previouseCount = 3;
          const myAnswerList = this.myAnswerList();

          for (let i = Math.max(0, myAnswerList.length - previouseCount); i < myAnswerList.length; i++) {
            if (i - 2 >= 0) {
              const previousMyAnswer = myAnswerList[i];
              previousConversationList.push(
                `

  **Previous Question:**
  \`\`\`
  ${data[i - 2]}
  \`\`\`


  **Previous Answer:**
  \`\`\`
  ${previousMyAnswer}
  \`\`\`

  `
              );
            }
          }
        }

        let previousCodingQuestion: any = undefined;

        const index = this.codeQuestionList().length - 1;

        if (this.codeQuestionList().length > 0) {
          previousCodingQuestion = {
            codeQuestion: this.codeQuestionList()[index],
            codeIdea: this.codeIdeaList()[index],
            codeSolution: this.codeSolutionList()[index],
            codeTestCases: this.codeTestCasesList()[index],
          };
        }

        const prompt = getInterviewPrompt(
          company,
          language,
          position,
          companyDetails,
          jobDescription,
          resume,
          lastContent,
          previousConversationList,
          {
            additionalDetails: this.interview!.subData.additionalDetails,
            selectedAnswerMode: this.answerModeOptions[this.selectedAnswerModeIndex ?? 0],
            previousCodingQuestion,
          }
        );

        // const model =
        //   performance ?? this.performanceOptions[this.selectedPerformanceIndex ?? 0] === InterviewPerformance.Speed
        //     ? OpenaiModel.gpt35
        //     : OpenaiModel.gpt4;
        const model = OpenaiModel.gpt4;

        const temperature =
          this.creativityOptions[this.selectedCreativityIndex ?? 0] === InterviewCreativity.Deterministic ? 0.2 : 0.8;

        this.aiResponseProcessStoppedTime = DateTime.local();

        this.streamStatus.set(OpenAIStreamStatus.generating);
        this.chatStreamStatus.set(OpenAIStreamStatus.generating);

        if (this.aiResponseList().length < data.length) {
          this.aiResponseList.update((data) => {
            return [...data, ''];
          });
        }

        if (this.myAnswerList().length < data.length) {
          this.myAnswerList.update((data) => {
            return [...data, ''];
          });
        }

        this.webSocketSendService.sendStreamingChat(this.interview!.id, [{ role: 'user', content: prompt }], {
          model,
          temperature,
          additionalId: data.length - 1,
        });

        return [...data, ''];
      } else {
        return data;
      }
    });
  }

  //#region Auto Scroll to Bottom
  private _shouldScrollToBottom: boolean = false;

  @ViewChild('myAnswer', { static: false })
  private myAnswerRef?: ElementRef;

  @ViewChild('aiResponse', { static: false })
  private aiResponseRef?: ElementRef;

  @ViewChild('testCases', { static: false })
  private testCasesRef?: ElementRef;

  @ViewChild('codeResponse', { static: false })
  private codeResponseRef?: ElementRef;

  @ViewChild('ideaResponse', { static: false })
  private ideaResponseRef?: ElementRef;

  @ViewChild('interviewer', { static: false })
  private interviewerRef?: ElementRef;

  private scrollAllToBottom(): void {
    this.scrollToBottom(this.myAnswerRef);
    this.scrollToBottom(this.aiResponseRef);
    this.scrollToBottom(this.testCasesRef);
    this.scrollToBottom(this.codeResponseRef);
    this.scrollToBottom(this.ideaResponseRef);
    this.scrollToBottom(this.interviewerRef);
  }

  private scrollToBottom(elementRef?: ElementRef): void {
    const scrollContainer = elementRef?.nativeElement;

    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }
  //#endregion
}

export enum OpenAIStreamStatus {
  pending = 'pending',
  generating = 'generating',
  failed = 'failed',
}

export enum ConnectionStatus {
  loading = 'loading',
  connected = 'connected',
  error = 'error',
  closed = 'closed',
  warning = 'warning',
}

export enum CodingResponseType {
  idea = 'Idea',
  code = 'Code',
  testCases = 'Test Cases',
}
